import { IMillProductDetail } from "../../types";
import { IStrandProduction } from "../../../../types";
import { formatDate } from "../../../../system/utils";

export interface IProdAggregate {
  min: number;
  max: number;
  sum: number;
  count: number;
  specMin: number | null;
  specMax: number | null;
  errors: string[];
}

export interface IBilletData extends IMillProductDetail{
  aggregateRecord: Record<string, IProdAggregate>;
}

export interface IProductionMachineData {
  productionMachine: IStrandProduction;
  billets: IBilletData[];
  aggregate: Record<string, IProdAggregate>;
}

export interface IDataProcessed {
  heatNumber: string;
  steelCode: string;
  millHeat: string;
  productionMachines: IProductionMachineData[];
  aggregate: Record<string, IProdAggregate>;
}

const getRowValue = (aggregate: IProdAggregate | null | undefined) => {
  let result = `${aggregate && aggregate.specMin ? aggregate.specMin.toFixed(2) : ""};`;
  result += `${aggregate && aggregate.specMax ? aggregate.specMax.toFixed(2) : ""};`;
  result += `${aggregate ? aggregate.min.toFixed(2) : ""};`;
  result += `${aggregate ? aggregate.max.toFixed(2) : ""};`;
  result += `${aggregate && aggregate.count ? (aggregate.sum / aggregate.count).toFixed(2) : ""}`;

  return result;
};


const getMachineRecipe = (pm: IProductionMachineData): string | null => pm.billets.length > 0 ? pm.billets[0].recipe : null;

const getOrderRecipe = (order: IDataProcessed): string | null => {
  const recipes = new Set<string>();

  order.productionMachines.forEach(pm => {
    const recipe = getMachineRecipe(pm);
    if (recipe) {
      recipes.add(recipe);
    }
  });

  return recipes.size > 0 ? [...recipes].join(" / ") : null;
};

export const exportByOrder = (dataProcessed: IDataProcessed[]): Blob => {
  let fileContent = "";

  fileContent += "Quality Rounds";
  fileContent += ";;;;Temp. Stand 4;;;;;Temp. Waterbox;;;;;Temp. Laying Head;;;;;\n";
  fileContent += "Order;Heat;Steel;Recipe;MIN SPEC;MAX SPEC;MIN;MAX;AVG;MIN SPEC;MAX SPEC;MIN;MAX;AVG;MIN SPEC;MAX SPEC;MIN;MAX;AVG;\n";

  dataProcessed.forEach(row => {
    const { millHeat, heatNumber, steelCode, aggregate } = row;
    const tempStand4 = aggregate["Temp. Stand 4"];
    const tempWaterBox = aggregate["Temp. Waterbox"];
    const tempLayingHead = aggregate["Temp. Laying Head"];
    const recipe = getOrderRecipe(row);

    fileContent += `${millHeat};${heatNumber};${steelCode};${recipe ?? "---"};`;
    fileContent += `${getRowValue(tempStand4)};${getRowValue(tempWaterBox)};${getRowValue(tempLayingHead)};\n`;
  });

  return new Blob([ fileContent ], { type: "text/csv" });
};


export const exportByMachine = (dataProcessed: IDataProcessed[]): Blob => {
  let fileContent = "";

  fileContent += "Quality Rounds";
  fileContent += ";;;;;;;;Temp. Stand 4;;;;;Temp. Waterbox;;;;;Temp. Laying Head;;;;;\n";
  fileContent += "Order;Heat;Steel;Recipe;Machine;Factory Day;Shift;Crew;MIN SPEC;MAX SPEC;MIN;MAX;AVG;MIN SPEC;MAX SPEC;MIN;MAX;AVG;MIN SPEC;MAX SPEC;MIN;MAX;AVG;\n";

  dataProcessed.forEach(row => {
    const { millHeat, heatNumber, steelCode, productionMachines } = row;

    productionMachines.forEach(pm => {
      const data = pm.productionMachine;
      const recipe = getMachineRecipe(pm);
      const tempStand4 = pm.aggregate["Temp. Stand 4"];
      const tempWaterBox = pm.aggregate["Temp. Waterbox"];
      const tempLayingHead = pm.aggregate["Temp. Laying Head"];

      fileContent += `${millHeat};${heatNumber};${steelCode};${recipe ?? "---"};`;
      fileContent += `${data.machine.name};${formatDate(data.shiftHistory.factoryDay)};${data.shiftHistory.name};${data.crew.name};`;
      fileContent += `${getRowValue(tempStand4)};${getRowValue(tempWaterBox)};${getRowValue(tempLayingHead)};\n`;
    });
  });

  return new Blob([ fileContent ], { type: "text/csv" });
};


export const exportByBillets = (dataProcessed: IDataProcessed[]): Blob => {
  let fileContent = "";

  fileContent += "Quality Rounds";
  fileContent += ";;;;;;;;;;;;;Temp. Stand 4;;;;;Temp. Waterbox;;;;;Temp. Laying Head;;;;;\n";
  fileContent += "Order;Heat;Steel;Recipe;Machine;Factory Day;Shift;Crew;Status;Billet;Length;Pressure;Weight;";
  fileContent += "MIN SPEC;MAX SPEC;MIN;MAX;AVG;MIN SPEC;MAX SPEC;MIN;MAX;AVG;MIN SPEC;MAX SPEC;MIN;MAX;AVG;\n";

  dataProcessed.forEach(row => {
    const { millHeat, heatNumber, steelCode, productionMachines } = row;

    productionMachines.forEach(pm => {
      const data = pm.productionMachine;

      pm.billets.sort((a, b) => (a.billet ?? 0) - (b.billet ?? 0)).forEach(b => {
        const tempStand4 = b.aggregateRecord["Temp. Stand 4"];
        const tempWaterBox = b.aggregateRecord["Temp. Waterbox"];
        const tempLayingHead = b.aggregateRecord["Temp. Laying Head"];

        let status = "GOOD";
        if (b.isCobble || b.isCutUp) {
          status = b.isCobble ? "Cobble" : "Cut Up";
        }

        fileContent += `${millHeat};${heatNumber};${steelCode};${b.recipe ?? "---"};`;
        fileContent += `${data.machine.name};${formatDate(data.shiftHistory.factoryDay)};${data.shiftHistory.name};${data.crew.name};`;
        fileContent += `${status};${b.billet};${b.compactingLength};${b.compactingPressure};${b.weight};`;
        fileContent += `${getRowValue(tempStand4)};${getRowValue(tempWaterBox)};${getRowValue(tempLayingHead)};\n`;
      });
    });

    // fileContent += "\n";
  });

  return new Blob([ fileContent ], { type: "text/csv" });
};


export const exportFanByOrder = (dataProcessed: IDataProcessed[]): Blob => {
  const columnList = [ "Speed", "1A", "1B", "2A", "2B", "3A", "3B", "4A", "5A", "5B" ];
  let fileContent = "";

  fileContent += "Quality Rounds";
  fileContent += ";;;;";
  columnList.forEach(c => fileContent += `${c.toUpperCase()};;;;;`);
  fileContent += "\n";
  fileContent += "Order;Heat;Steel;Recipe;";
  columnList.forEach(() => fileContent += "MIN SPEC;MAX SPEC;MIN;MAX;AVG;");
  fileContent += "\n";

  dataProcessed.forEach(row => {
    const { millHeat, heatNumber, steelCode, aggregate } = row;

    fileContent += `${millHeat};${heatNumber};${steelCode};${getOrderRecipe(row) ?? "---"};`;
    columnList.forEach(c => fileContent += `${getRowValue(aggregate[c])};`);
    fileContent += "\n";
  });

  return new Blob([ fileContent ], { type: "text/csv" });
};


export const exportFanByMachine = (dataProcessed: IDataProcessed[]): Blob => {
  const columnList = [ "Speed", "1A", "1B", "2A", "2B", "3A", "3B", "4A", "5A", "5B" ];
  let fileContent = "";

  fileContent += "Quality Rounds";
  fileContent += ";;;;;;;;";
  columnList.forEach(c => fileContent += `${c.toUpperCase()};;;;;`);
  fileContent += "\n";
  fileContent += "Order;Heat;Steel;Recipe;Machine;Factory Day;Shift;Crew;";
  columnList.forEach(() => fileContent += "MIN SPEC;MAX SPEC;MIN;MAX;AVG;");
  fileContent += "\n";

  dataProcessed.forEach(row => {
    const { millHeat, heatNumber, steelCode, productionMachines } = row;

    productionMachines.forEach(pm => {
      const data = pm.productionMachine;

      fileContent += `${millHeat};${heatNumber};${steelCode};${getMachineRecipe(pm) ?? "---"};`;
      fileContent += `${data.machine.name};${formatDate(data.shiftHistory.factoryDay)};${data.shiftHistory.name};${data.crew.name};`;
      columnList.forEach(c => fileContent += `${getRowValue(pm.aggregate[c])};`);
      fileContent += "\n";
    });
  });

  return new Blob([ fileContent ], { type: "text/csv" });
};


export const exportFanByBillets = (dataProcessed: IDataProcessed[]): Blob => {
  const columnList = [ "Speed", "1A", "1B", "2A", "2B", "3A", "3B", "4A", "5A", "5B" ];
  let fileContent = "";

  fileContent += "Quality Rounds";
  fileContent += ";;;;;;;;;;;;;";
  columnList.forEach(c => fileContent += `${c.toUpperCase()};;;;;`);
  fileContent += "\n";
  fileContent += "Order;Heat;Steel;Recipe;Machine;Factory Day;Shift;Crew;Status;Billet;Length;Pressure;Weight;";
  columnList.forEach(() => fileContent += "MIN SPEC;MAX SPEC;MIN;MAX;AVG;");
  fileContent += "\n";

  dataProcessed.forEach(row => {
    const { millHeat, heatNumber, steelCode, productionMachines } = row;

    productionMachines.forEach(pm => {
      const data = pm.productionMachine;

      pm.billets.sort((a, b) => (a.billet ?? 0) - (b.billet ?? 0)).forEach(b => {
        let status = "GOOD";
        if (b.isCobble || b.isCutUp) {
          status = b.isCobble ? "Cobble" : "Cut Up";
        }

        fileContent += `${millHeat};${heatNumber};${steelCode};${b.recipe ?? "---"};`;
        fileContent += `${data.machine.name};${formatDate(data.shiftHistory.factoryDay)};${data.shiftHistory.name};${data.crew.name};`;
        fileContent += `${status};${b.billet};${b.compactingLength};${b.compactingPressure};${b.weight};`;
        columnList.forEach(c => fileContent += `${getRowValue(b.aggregateRecord[c])};`);
        fileContent += "\n";
      });
    });

  });

  return new Blob([ fileContent ], { type: "text/csv" });
};
